/**
 * DashboardTagWidget - Compact tag display for Dashboard
 * Shows user's @tag with copy and share functionality
 */
import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AtSign, Copy, Share2, ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';

const DashboardTagWidget = () => {
  const [loading, setLoading] = useState(true);
  const [tagData, setTagData] = useState(null);

  useEffect(() => {
    const fetchTag = async () => {
      try {
        const { data } = await api.get('/v1/tags/me');
        setTagData(data.has_tag ? data : null);
      } catch (err) {
        console.error('Failed to fetch tag:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTag();
  }, []);

  const copyTag = () => {
    navigator.clipboard.writeText(tagData?.tag_display || '');
    toast.success('Tag copied!');
  };

  const shareProfile = async () => {
    if (navigator.share && tagData) {
      try {
        await navigator.share({
          title: 'Pay me on ParachPay',
          text: `Send money to ${tagData.tag_display}`,
          url: tagData.profile_url
        });
      } catch (err) {
        navigator.clipboard.writeText(tagData?.profile_url || '');
        toast.success('Link copied!');
      }
    } else {
      navigator.clipboard.writeText(tagData?.profile_url || '');
      toast.success('Link copied!');
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-r from-violet-600/10 to-purple-600/10 border-violet-500/20">
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
        </CardContent>
      </Card>
    );
  }

  if (!tagData) {
    // No tag - show claim CTA
    return (
      <Card className="bg-gradient-to-r from-violet-600/10 to-purple-600/10 border-violet-500/20" data-testid="tag-widget-claim">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/20 rounded-lg">
                <AtSign className="h-5 w-5 text-violet-500" />
              </div>
              <div>
                <p className="font-semibold text-sm">Claim your @tag</p>
                <p className="text-xs text-muted-foreground">Get paid with just your @name</p>
              </div>
            </div>
            <Button asChild size="sm" className="bg-violet-600 hover:bg-violet-700">
              <Link to="/settings">
                Claim Now
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Has tag - show it
  return (
    <Card className="bg-gradient-to-r from-emerald-600/10 to-teal-600/10 border-emerald-500/20" data-testid="tag-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <AtSign className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Your ParachPay Tag</p>
              <p className="font-bold font-mono text-lg text-emerald-600 dark:text-emerald-400">
                {tagData.tag_display}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={copyTag} className="h-8 w-8" data-testid="copy-tag-btn">
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={shareProfile} className="h-8 w-8" data-testid="share-tag-btn">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardTagWidget;
